import {
  get,
  limitToFirst,
  orderByChild,
  query,
  ref,
  startAfter,
} from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import {
  createEmptyConfirmations,
  createEmptyOnboardingSession,
  createEmptyPersonContact,
} from "../pages/PreActivation/agencyFormConfig";
import {
  AGENCY_LEGACY_FIELD_KEYS,
  getOrganizationTypeFromRecord,
} from "../pages/PreActivation/preActivationFormValues";

import { LaunchError } from "../utils/errors";
import { store } from "../app/store";

const PRE_ACTIVATION_PATH = "preActivation";

/** Filas por petición a RTDB (se piden PAGE_SIZE + 1 para saber si hay página siguiente). */
export const PRE_ACTIVATION_PAGE_SIZE = 10;
const PAGE_FETCH = PRE_ACTIVATION_PAGE_SIZE + 1;

const str = (v) => (v == null ? "" : String(v));
const arr = (v) => (Array.isArray(v) ? v : []);
const bool = (v) => v === true;

const normalizePerson = (raw = {}) => ({
  ...createEmptyPersonContact(),
  fullName: str(raw.fullName),
  jobTitle: str(raw.jobTitle),
  email: str(raw.email),
});

const normalizeConfirmations = (raw = {}) => ({
  ...createEmptyConfirmations(),
  singleBrand: bool(raw.singleBrand),
  accountAdmin: bool(raw.accountAdmin),
  reschedule: bool(raw.reschedule),
});

const normalizeOnboardingSession = (raw) => {
  const source = raw && typeof raw === "object" ? raw : {};
  return {
    ...createEmptyOnboardingSession(),
    startTime: str(source.startTime),
    endTime: str(source.endTime),
    uid: str(source.uid),
    title: str(source.title),
  };
};

/**
 * Normalizes RTDB payload to react-hook-form defaultValues for PreActivation.
 */
export function normalizePreActivationRecord(raw = {}) {
  const organizationType = getOrganizationTypeFromRecord(raw);

  const normalized = {
    organizationType,
    fullName: str(raw.fullName),
    email: str(raw.email),
    businessName: str(raw.businessName),
    jobTitle: str(raw.jobTitle),
    whatsApp: str(raw.whatsApp),
    websiteUrl: str(raw.websiteUrl),
    whatYouSell: str(raw.whatYouSell),
    averagePrice: str(raw.averagePrice),
    idealCustomer: str(raw.idealCustomer),
    whereCustomerBuys: arr(raw.whereCustomerBuys),
    hasAds: str(raw.hasAds),
    adPlatforms: arr(raw.adPlatforms),
    adPlatformsOther: str(raw.adPlatformsOther),
    monthlyAdSpend: str(raw.monthlyAdSpend),
    currentMetrics: str(raw.currentMetrics),
    targetCpa: str(raw.targetCpa),
    bestCampaignMessages: str(raw.bestCampaignMessages),
    monthlySales: str(raw.monthlySales),
    salesGoal60to90: str(raw.salesGoal60to90),
    buzzlyFocus: str(raw.buzzlyFocus),
    creativeMaterialLevel: str(raw.creativeMaterialLevel),
    creativeMaterialLocation: str(raw.creativeMaterialLocation),
    additionalMeetingNotes: str(raw.additionalMeetingNotes),
    meetingAccess: arr(raw.meetingAccess),
    accessNotReady: str(raw.accessNotReady),
    backupContact: normalizePerson(raw.backupContact),
    leadershipContact: normalizePerson(raw.leadershipContact),
    agencyModules: arr(raw.agencyModules),
    agencyAdPlatforms: arr(raw.agencyAdPlatforms),
    teamFamiliarity: str(raw.teamFamiliarity),
    sessionBrandName: str(raw.sessionBrandName),
    confirmations: normalizeConfirmations(raw.confirmations),
    onboardingSession: normalizeOnboardingSession(raw.onboardingSession),
  };

  AGENCY_LEGACY_FIELD_KEYS.forEach((key) => {
    if (key === "agencyAdPlatforms") return;
    if (raw[key] != null) {
      normalized[key] = Array.isArray(raw[key]) ? arr(raw[key]) : str(raw[key]);
    }
  });

  return normalized;
}

export function preActivationSnapshotToRows(snapshotVal) {
  if (!snapshotVal || typeof snapshotVal !== "object") return [];
  return Object.entries(snapshotVal)
    .map(([formId, data]) => {
      const n = normalizePreActivationRecord(data);
      return {
        formId,
        businessName: n.businessName,
        fullName: n.fullName,
        email: n.email,
        whatsApp: n.whatsApp,
        organizationType: n.organizationType,
      };
    })
    .sort((a, b) => {
      const A = `${a.businessName}${a.email}`.toLowerCase();
      const B = `${b.businessName}${b.email}`.toLowerCase();
      return A.localeCompare(B);
    });
}

/**
 * @param {(rows: ReturnType<typeof preActivationSnapshotToRows>) => void} onData
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function listenPreActivationSubmissions(onData, onError) {
  const dbRef = ref(db, PRE_ACTIVATION_PATH);
  return onValue(
    dbRef,
    (snap) => {
      try {
        onData(preActivationSnapshotToRows(snap.val()));
      } catch (e) {
        store.dispatch(
          LaunchError(e, false, [], {
            functionName: "listenPreActivationSubmissions",
            capture: "exception",
          })
        );
        onError?.(e);
      }
    },
    (err) => onError?.(err)
  );
}

export async function fetchPreActivationByFormId(formId) {
  if (!formId) return null;
  const dbRef = ref(db, `${PRE_ACTIVATION_PATH}/${formId}`);
  const snap = await get(dbRef);
  if (!snap.exists()) return null;
  return normalizePreActivationRecord(snap.val());
}

export async function fetchPreActivationPage(cursorKey = null) {
  const baseRef = ref(db, PRE_ACTIVATION_PATH);
  const hasCursor =
    cursorKey?.value != null && typeof cursorKey?.id === "string";
  const pageQuery = hasCursor
    ? query(
        baseRef,
        orderByChild("creationTimeNegative"),
        startAfter(cursorKey.value, cursorKey.id),
        limitToFirst(PAGE_FETCH)
      )
    : query(
        baseRef,
        orderByChild("creationTimeNegative"),
        limitToFirst(PAGE_FETCH)
      );

  const snap = await get(pageQuery);
  if (!snap.exists()) {
    return { rows: [], cursorAfter: null, hasMore: false };
  }

  const entries = [];
  snap.forEach((child) => {
    entries.push([child.key, child.val()]);
  });

  const hasMore = entries.length > PRE_ACTIVATION_PAGE_SIZE;
  const slice = hasMore ? entries.slice(0, PRE_ACTIVATION_PAGE_SIZE) : entries;

  const rows = slice.map(([formId, data]) => {
    const n = normalizePreActivationRecord(data);
    const creationTimeNegativeRaw = Number(data?.creationTimeNegative);
    const creationTimeNegative = Number.isNaN(creationTimeNegativeRaw)
      ? 0
      : creationTimeNegativeRaw;
    return {
      formId,
      businessName: n.businessName,
      fullName: n.fullName,
      email: n.email,
      whatsApp: n.whatsApp,
      organizationType: n.organizationType,
      creationTime:
        data?.creationTime ??
        (creationTimeNegative ? -creationTimeNegative : null),
      creationTimeNegative,
    };
  });
  const cursorAfter =
    rows.length > 0
      ? {
          value: rows[rows.length - 1].creationTimeNegative,
          id: rows[rows.length - 1].formId,
        }
      : null;

  return { rows, cursorAfter, hasMore };
}
